import { Box, CircularProgress, PaletteColor, Theme, Typography, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Accordion, FlexyButton } from '@europrocurement/flexy-components';
import { lockIcon } from '@europrocurement/l2d-icons';
import { SxProps } from '@mui/system';
import { SocieteJsonldSocieteReadItem } from '@europrocurement/l2d-domain/openApi/ApiSociete';
import { usePanierPay } from '@europrocurement/l2d-modules/modules/Paiement';
import useSocieteInfo from '../../../hooks/useSocieteInfo';

export type ModalPaiementProps = {
    amount: number;
    ids: Array<number>;
    dossierNumero?: string;
    type: 'collection' | 'facture';
    cbPayActif?: boolean;
    virementPayActif?: boolean;
    chequePayActif?: boolean;
    typoBackground?: boolean;
};

const ModalPaiement: React.FunctionComponent<ModalPaiementProps> = function (props) {
    const {
        amount,
        dossierNumero,
        ids,
        type,
        cbPayActif = true,
        virementPayActif = true,
        chequePayActif = true,
        typoBackground = true,
    } = props;
    const { getSocieteData } = useSocieteInfo();
    const { palette } = useTheme();

    const [societeData, setSocieteData] = useState<SocieteJsonldSocieteReadItem>();

    const { navigateFacturePay, navigateCollectionPay } = usePanierPay();
    useEffect(() => {
        getSocieteData().then(setSocieteData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const wrapperInvoicePaymentSx = {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        flexDirection: 'column',
        minWidth: '570px',
    };

    const labelSx = {
        fontSize: '15px',
        fontWeight: '600',
        lineHeight: '21px',
    };

    const labelLineSx: SxProps<Theme> = {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        '& span': {
            whiteSpace: 'nowrap',
            position: 'relative',
            zIndex: 1,
            backgroundColor: typoBackground ? 'background.paper' : undefined,
            paddingRight: 1,
        },
        '&::after': {
            content: '""',
            flexGrow: 1,
            height: '1px',
            backgroundColor: 'divider',
        },
    };

    const itemStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
    };
    const itemLabelStyle: React.CSSProperties = {
        width: '150px',
        color: 'text.gris50',
    };
    const itemInfoStyle: React.CSSProperties = {
        width: 'calc(100% - 150px)',
        color: 'text.gris70',
    };
    return societeData ? (
        <Box
            sx={wrapperInvoicePaymentSx}
            data-testid="invoice-payment"
        >
            <Box sx={{ textAlign: 'left', width: '100%' }}>
                <Typography sx={labelSx}>Paiement</Typography>
                <Typography
                    typography="body2"
                    // sx={{ marginBottom: '0px' }}
                >
                    Restant dû (TTC)
                </Typography>
                <Typography typography="l2dh4">
                    {(Math.floor(amount * 100) / 100).toFixed(2)}&euro;
                </Typography>
            </Box>
            {cbPayActif && !virementPayActif && !chequePayActif && (
                <>
                    <Typography
                        sx={labelLineSx}
                        color="secondary"
                    >
                        <span>Paiement par carte</span>
                    </Typography>

                    <FlexyButton
                        variant="contained"
                        sx={{ width: '100%' }}
                        color={palette.primary.dark}
                        onClick={() => {
                            if (type === 'facture') {
                                navigateFacturePay(ids as unknown as string[]);
                            } else {
                                navigateCollectionPay(ids as unknown as string[]);
                            }
                        }}
                        icon={lockIcon}
                        label="Payer par CB"
                    />
                    <Box textAlign="center">
                        <Typography
                            typography="l2dtableCell"
                            color="text.gris70"
                        >
                            Paiement sécurisé et données protégées
                        </Typography>
                    </Box>
                </>
            )}
            {cbPayActif && (virementPayActif || chequePayActif) && (
                <Accordion
                    key="accordion-virement"
                    expandColor={palette.text.primary as unknown as PaletteColor}
                    title={
                        <Typography
                            sx={labelLineSx}
                            color="secondary"
                        >
                            <span>Paiement par carte</span>
                        </Typography>
                    }
                    expandLabel={null}
                    // expandColor={palette.primary}
                    expanded
                    sx={{
                        borderWidth: '0px',
                        boxShadow: '',
                    }}
                    headerSx={{
                        borderBottomWidth: '0px',
                        padding: '25px 28px 0px 15px',
                    }}
                >
                    <FlexyButton
                        variant="contained"
                        sx={{ width: '100%' }}
                        color={palette.primary.dark}
                        onClick={() => {
                            if (type === 'facture') {
                                navigateFacturePay(ids as unknown as string[]);
                            } else {
                                navigateCollectionPay(ids as unknown as string[]);
                            }
                        }}
                        icon={lockIcon}
                        label="Payer par CB"
                    />
                    <Box textAlign="center">
                        <Typography
                            typography="l2dtableCell"
                            color="text.gris70"
                        >
                            Paiement sécurisé et données protégées
                        </Typography>
                    </Box>
                </Accordion>
            )}
            {virementPayActif && (
                <Accordion
                    key="accordion-virement"
                    expandColor={palette.text.primary as unknown as PaletteColor}
                    title={
                        <Typography
                            sx={labelLineSx}
                            color="secondary"
                        >
                            <span>Paiement par virement</span>
                        </Typography>
                    }
                    expandLabel={null}
                    // expandColor={palette.primary}
                    expanded={false}
                    sx={{
                        borderWidth: '0px',
                        boxShadow: '',
                    }}
                    headerSx={{
                        borderBottomWidth: '0px',
                        padding: '25px 28px 0px 15px',
                    }}
                >
                    <Box>
                        <Box sx={itemStyle}>
                            <Typography
                                variant="l2dtableCell"
                                sx={itemLabelStyle}
                            >
                                Iban
                            </Typography>
                            <Typography
                                variant="l2dtableCell"
                                sx={itemInfoStyle}
                            >
                                {societeData.compteBanque}
                            </Typography>
                        </Box>

                        <Box sx={itemStyle}>
                            <Typography
                                variant="l2dtableCell"
                                sx={itemLabelStyle}
                            >
                                BIC
                            </Typography>
                            <Typography
                                variant="l2dtableCell"
                                sx={itemInfoStyle}
                            >
                                {societeData.codeBic}
                            </Typography>
                        </Box>

                        {dossierNumero ? (
                            <Box sx={itemStyle}>
                                <Typography
                                    variant="l2dtableCell"
                                    sx={itemLabelStyle}
                                >
                                    Libellé
                                </Typography>
                                <Typography
                                    variant="l2dtableCell"
                                    sx={itemInfoStyle}
                                >{`Libellé Dossier de formalités n°
                                ${dossierNumero},
                                 (et numéro de facture)`}</Typography>
                            </Box>
                        ) : null}
                    </Box>
                </Accordion>
            )}
            {chequePayActif && (
                <Accordion
                    key="accordion-cheque"
                    expandColor={palette.text.primary as unknown as PaletteColor}
                    title={
                        <Typography
                            sx={labelLineSx}
                            color="secondary"
                        >
                            <span>Paiement par chèque</span>
                        </Typography>
                    }
                    expandLabel={null}
                    expanded={false}
                    sx={{
                        borderWidth: '0px',
                        boxShadow: '',
                    }}
                    headerSx={{ borderBottomWidth: '0px', padding: '25px 28px 0px 15px' }}
                >
                    <Box sx={itemStyle}>
                        <Typography
                            variant="l2dtableCell"
                            sx={itemLabelStyle}
                        >
                            Ordre
                        </Typography>
                        <Typography
                            variant="l2dtableCell"
                            sx={itemInfoStyle}
                        >
                            {societeData.raisonSociale}
                        </Typography>
                    </Box>
                    <Box sx={itemStyle}>
                        <Typography
                            variant="l2dtableCell"
                            sx={itemLabelStyle}
                        >
                            AU DOS
                        </Typography>
                        <Box
                            sx={{ width: 'calc(100% - 150px)' }}
                        >{`Dossier de formalités n° ${dossierNumero} (et numéro de facture)`}</Box>
                    </Box>
                    <Box sx={itemStyle}>
                        <Typography
                            variant="l2dtableCell"
                            sx={itemLabelStyle}
                        >
                            Adresse
                        </Typography>
                        <Typography
                            variant="l2dtableCell"
                            sx={itemInfoStyle}
                        >
                            {[
                                societeData.adresse1,
                                societeData.adresse2,
                                societeData.adresse3,
                                societeData.adresse4,
                                societeData.codePostal,
                                societeData.ville,
                            ]
                                .filter(Boolean)
                                .join(' ')}
                        </Typography>
                    </Box>
                </Accordion>
            )}
        </Box>
    ) : (
        <CircularProgress />
    );
};

export default ModalPaiement;
